import React from 'react';
import { Icons } from '../../../components/ui/Icons';
import { useStagger } from '../hooks/useScrollReveal';

const steps = [
  {
    num: '01',
    title: 'Connect',
    desc: 'One-click OAuth. Link Gmail, Calendar, Drive, Notion, Slack & more. No API keys.',
    visual: (
      <div className="flex items-center gap-2 mt-4">
        {[Icons.Gmail, Icons.GoogleCalendar, Icons.GoogleDrive,
          (p: any) => <img src="https://upload.wikimedia.org/wikipedia/commons/4/45/Notion_app_logo.png" alt="N" className={p.className} />,
          (p: any) => <img src="https://cdn-icons-png.flaticon.com/128/3800/3800024.png" alt="S" className={p.className} />,
        ].map((Ic, j) => (
          <div key={j} className="w-9 h-9 bg-white rounded-xl border border-black/[0.05] flex items-center justify-center shadow-[0_1px_3px_rgba(0,0,0,0.03)] hover:scale-110 transition-transform">
            <Ic className="w-4 h-4 object-contain" />
          </div>
        ))}
      </div>
    ),
  },
  {
    num: '02',
    title: 'Command',
    desc: 'Describe what you need in plain English. Neura understands context, intent & urgency.',
    visual: (
      <div className="mt-4 bg-[#f8f8f8] rounded-xl px-4 py-3 border border-black/[0.04]">
        <p className="text-[12px] text-black/35 italic font-normal leading-relaxed">"Schedule a recap next Tuesday at 3 PM and invite the product team."</p>
      </div>
    ),
  },
  {
    num: '03',
    title: 'Approve',
    desc: 'Review every action before it fires. Full audit trails. You stay in complete control.',
    visual: (
      <div className="mt-4 flex items-center gap-2">
        <span className="w-2 h-2 rounded-full bg-emerald-400 shadow-[0_0_6px_rgba(52,211,153,0.5)]" />
        <span className="text-[12px] font-semibold text-emerald-600/70">You stay in control — always</span>
      </div>
    ),
  },
];

const LandingHowItWorks: React.FC = () => {
  const ref = useStagger(180);

  return (
    <section id="how-it-works" className="relative py-10 md:py-14 bg-[#fafafa] overflow-hidden scroll-mt-20 grain-overlay">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="max-w-xl mb-16 md:mb-20">
          <p className="text-[11px] font-semibold text-black/30 tracking-[0.2em] uppercase mb-4">How it works</p>
          <h2 className="text-[32px] md:text-[46px] font-semibold tracking-[-0.04em] text-black leading-[1.1]">
            Three steps.<br />That's it.
          </h2>
        </div>

        {/* Steps — horizontal on desktop */}
        <div ref={ref} className="grid grid-cols-1 md:grid-cols-3 gap-6 relative">
          {/* Connecting line */}
          <div className="hidden md:block absolute top-[42px] left-[15%] right-[15%] h-px bg-gradient-to-r from-black/[0.06] via-black/[0.1] to-black/[0.06]" />

          {steps.map((s, i) => (
            <div key={i} className={`sr delay-${(i + 1) * 2} relative`}>
              {/* Step number */}
              <div className="w-10 h-10 rounded-xl bg-black text-white text-[13px] font-bold flex items-center justify-center mb-6 relative z-10 shadow-[0_2px_8px_rgba(0,0,0,0.12)]">
                {s.num}
              </div>

              {/* Card */}
              <div className="bg-white rounded-2xl p-6 border border-black/[0.05] hover-lift">
                <h3 className="text-[20px] md:text-[32px] font-medium text-black tracking-[-0.02em] mb-2">{s.title}</h3>
                <p className="text-[14px] text-black/40 font-normal leading-relaxed">{s.desc}</p>
                {s.visual}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default LandingHowItWorks;
